import React from "react";
import { ArrowUpRight, MapPin } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden"
    >
      {/* Background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-blue-100/40 blur-3xl" />
        <div className="absolute top-1/2 -left-20 w-[400px] h-[400px] rounded-full bg-slate-100 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left content */}
        <div className="lg:col-span-7 fade-up">
          <div className="flex items-center gap-3 mb-8">
            <span className="overline">Toronto · Ontario · Canada</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-600">
              <MapPin size={12} /> EST. 2026
            </span>
          </div>

          <h1
            className="font-display text-[clamp(3rem,8vw,7.5rem)] font-black leading-[0.88] tracking-tighter"
            data-testid="hero-headline"
          >
            YOUR BRAND.
            <br />
            <span className="relative inline-block">
              EVERY
              <svg
                viewBox="0 0 220 12"
                className="absolute -bottom-2 left-0 w-full h-3"
                preserveAspectRatio="none"
              >
                <path
                  d="M2 6 Q60 2 110 6 T218 6"
                  stroke="#0A2A6B"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>{" "}
            <span className="font-editorial italic font-medium">sip.</span>
          </h1>

          <p className="mt-10 max-w-xl text-lg sm:text-xl text-slate-700 leading-relaxed">
            We are a marketing company. We put <b>your brand</b> on premium
            custom-label water bottles — turning every hand, table and event
            into a <span className="font-editorial italic">walking billboard</span>.
            Restaurants, gyms, real estate, activations and beyond.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-quote"
              className="tq-btn tq-btn-primary"
            >
              Request a Quote
              <ArrowUpRight size={16} />
            </a>
            <a
              href="#use-cases"
              data-testid="hero-cta-usecases"
              className="tq-btn tq-btn-ghost"
            >
              See Use Cases
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
            <div>
              <div className="font-display text-3xl font-black">1,728</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                Bottles / skid
              </div>
            </div>
            <div>
              <div className="font-display text-3xl font-black">77%</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                Avg scan rate
              </div>
            </div>
            <div>
              <div className="font-display text-3xl font-black">14 days</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                Design → delivery
              </div>
            </div>
          </div>
        </div>

        {/* Right bottle */}
        <div className="lg:col-span-5 relative flex justify-center items-center">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[520px] h-[520px] rounded-full border border-slate-200" />
            <div className="absolute w-[380px] h-[380px] rounded-full border border-slate-200" />
            <div className="absolute w-[240px] h-[240px] rounded-full bg-blue-50" />
          </div>
          <div className="relative z-10 float-bottle">
            <img
              src="/bottle-hero.webp"
              alt="Tactiq custom label water bottle — Your Logo Goes Here"
              className="w-[240px] sm:w-[300px] lg:w-[360px] h-auto bottle-shadow select-none"
              draggable={false}
            />
          </div>
          <div className="absolute bottom-6 right-0 max-w-[210px] tq-glass rounded-2xl p-4 hidden sm:block">
            <div className="overline mb-1">Live</div>
            <div className="text-sm font-semibold leading-snug text-slate-900">
              24,000 bottles shipping to TIFF activation this Friday.
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="relative mt-24 overflow-hidden border-y border-slate-200 bg-white py-5">
        <div className="flex marquee whitespace-nowrap font-display font-black tracking-tighter text-3xl uppercase text-slate-900 gap-12">
          {Array.from({ length: 4 }).map((_, i) => (
            <React.Fragment key={i}>
              <span>Restaurants</span>
              <span className="text-[var(--tq-navy)]">/</span>
              <span>Gyms</span>
              <span className="text-[var(--tq-navy)]">/</span>
              <span>Real Estate</span>
              <span className="text-[var(--tq-navy)]">/</span>
              <span>Corporate</span>
              <span className="text-[var(--tq-navy)]">/</span>
              <span>Events</span>
              <span className="text-[var(--tq-navy)]">/</span>
              <span>Schools</span>
              <span className="text-[var(--tq-navy)]">/</span>
              <span>Hotels</span>
              <span className="text-[var(--tq-navy)]">/</span>
              <span>Healthcare</span>
              <span className="text-[var(--tq-navy)]">/</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};
